import React, {useCallback} from 'react'
import {TypeOfRepresentation} from '@modifi/enums'
import {
  LEGAL_REPRESENTATIVE_AADHAAR_NUMBER,
  LEGAL_REPRESENTATIVE_POSITION,
  LEGAL_REPRESENTATIVE_TYPE_OF_REPRESENTATION,
  LEGAL_REPRESENTATIVE_VISA_DOCUMENT,
  LEGAL_REPRESENTATIVE_UTILITY_BILL_DOCUMENT,
  LEGAL_REPRESENTATIVE_DIRECTOR_IDENTIFICATION_NUMBER,
  LEGAL_REPRESENTATIVE_PAN_CARD_DOCUMENT,
  COMPANY_LEGAL_REPRESENTATIVES,
  PersonDocumentUploadWithInputContainer,
  InputPureWithInputContainer,
  SelectPureWithInputContainer,
  FormDataFilter,
  shouldRenderLegalRepPanCardDocumentFilter,
  shouldRenderLegalRepVisaDocumentFilter,
  shouldRenderLegalRepPositionFilter,
  shouldRenderLegalRepTypeOfRepresentationFilter,
  shouldRenderLegalRepAadhaarNumberFilter,
  shouldRenderLegalRepDirectorIdentificationNumberFilter,
  shouldRenderLegalRepPoliticallyExposedFilter,
  shouldRenderLegalRepUtilityBillDocument,
  ExistingPersonInputRow,
  AddressForm,
  InputTooltipInfo,
} from '@modifi/plugin-onboarding'
import {Col, Row, H5} from '@modifi/ui'

import {PoliticallyExposedInputRow, IsSignatoryInputRow} from '../input-rows'
import PersonForm from '../../../../../features/person-form'
import {
  FormInputComponentCol,
  FormUploadComponentCol,
  ExistingFilesList,
} from '../../../../../plugins/onboarding'
import {FormRow} from '../../../../../ui/form'
import ProgressSteps from '../../../../../component/progress-steps'
import {getCompanyRepProgressStepsLabels} from '../../../utils'
import {SelectOption, TranslateFn} from '../../../../../types/common'

type LegalRepresentativesFormContentProps = {
  t: TranslateFn
  isNewLegalRepresentative: boolean
  selectPersonAsDataSource: (value: string) => void
  dataSourceOptions: SelectOption[]
  currentPage: number
  hasAnyRoleFieldItem: boolean
}

const LegalRepresentativesFormContent: React.FC<LegalRepresentativesFormContentProps> = ({
  isNewLegalRepresentative,
  selectPersonAsDataSource,
  t,
  dataSourceOptions,
  currentPage,
  hasAnyRoleFieldItem,
}) => {
  const getTypesOfRepresentationOptions = useCallback(
    () =>
      Object.values(TypeOfRepresentation).map(type => ({
        value: type,
        label: t(`type-of-representation.${type}`),
      })),
    [t]
  )

  const showPersonForm = useCallback(() => {
    if (isNewLegalRepresentative) {
      if (
        (dataSourceOptions.length > 0 && currentPage === 2) ||
        (dataSourceOptions.length < 1 && currentPage === 1)
      ) {
        return true
      }
    } else if (currentPage === 1) {
      return true
    }
    return false
  }, [currentPage, dataSourceOptions?.length, isNewLegalRepresentative])

  const showRoleFields = useCallback(() => {
    if (
      (currentPage === 2 && dataSourceOptions.length > 1) ||
      (currentPage === 1 && dataSourceOptions.length < 1)
    ) {
      return true
    }
    return false
  }, [currentPage, dataSourceOptions?.length])

  const showResidenceFields = useCallback(() => {
    if (currentPage === 3 || (currentPage === 2 && dataSourceOptions.length < 1)) {
      return true
    }
    return false
  }, [currentPage, dataSourceOptions?.length])

  return (
    <>
      <ProgressSteps
        totalSteps={
          dataSourceOptions.length > 0 && isNewLegalRepresentative && hasAnyRoleFieldItem ? 3 : 2
        }
        currentStep={currentPage}
        labelSteps={getCompanyRepProgressStepsLabels(
          dataSourceOptions.length > 0 && !!isNewLegalRepresentative
        )}
      />

      {isNewLegalRepresentative && dataSourceOptions.length > 0 && currentPage === 1 && (
        <ExistingPersonInputRow
          t={t}
          onSelectPerson={selectPersonAsDataSource}
          options={dataSourceOptions}
          personRole={COMPANY_LEGAL_REPRESENTATIVES}
        />
      )}

      {showPersonForm() && (
        <>
          <IsSignatoryInputRow t={t} />
          <PersonForm t={t} formType={COMPANY_LEGAL_REPRESENTATIVES} />{' '}
        </>
      )}

      {showRoleFields() && (
        <>
          <FormRow mb={16}>
            <FormInputComponentCol
              filterFunc={shouldRenderLegalRepPositionFilter}
              t={t}
              inputName={LEGAL_REPRESENTATIVE_POSITION}
              inputComponent={InputPureWithInputContainer}
              inputProps={{
                labelAddition: <InputTooltipInfo inputName={LEGAL_REPRESENTATIVE_POSITION} t={t} />,
              }}
            />

            <FormInputComponentCol
              filterFunc={shouldRenderLegalRepTypeOfRepresentationFilter}
              t={t}
              inputName={LEGAL_REPRESENTATIVE_TYPE_OF_REPRESENTATION}
              inputComponent={SelectPureWithInputContainer}
              options={getTypesOfRepresentationOptions()}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo inputName={LEGAL_REPRESENTATIVE_TYPE_OF_REPRESENTATION} t={t} />
                ),
              }}
            />

            <FormInputComponentCol
              filterFunc={shouldRenderLegalRepAadhaarNumberFilter}
              t={t}
              inputName={LEGAL_REPRESENTATIVE_AADHAAR_NUMBER}
              inputComponent={InputPureWithInputContainer}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo inputName={LEGAL_REPRESENTATIVE_AADHAAR_NUMBER} t={t} />
                ),
              }}
            />

            <FormUploadComponentCol
              t={t}
              inputName={LEGAL_REPRESENTATIVE_PAN_CARD_DOCUMENT}
              uploadFormComponent={PersonDocumentUploadWithInputContainer}
              filterFunc={shouldRenderLegalRepPanCardDocumentFilter}
              colProps={{md: 12}}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo inputName={LEGAL_REPRESENTATIVE_PAN_CARD_DOCUMENT} t={t} />
                ),
                components: {
                  existingFilesList: ExistingFilesList,
                },
              }}
            />

            <FormInputComponentCol
              filterFunc={shouldRenderLegalRepDirectorIdentificationNumberFilter}
              t={t}
              inputName={LEGAL_REPRESENTATIVE_DIRECTOR_IDENTIFICATION_NUMBER}
              inputComponent={InputPureWithInputContainer}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo
                    inputName={LEGAL_REPRESENTATIVE_DIRECTOR_IDENTIFICATION_NUMBER}
                    t={t}
                  />
                ),
              }}
            />
          </FormRow>

          <FormDataFilter filterFunc={shouldRenderLegalRepPoliticallyExposedFilter}>
            <PoliticallyExposedInputRow t={t} />
          </FormDataFilter>
        </>
      )}

      {showResidenceFields() && (
        <>
          <Row mb={16} mt={8}>
            <Col>
              <H5>{t('person-form.address.headline')}</H5>
            </Col>
          </Row>
          <Row>
            <Col>
              <AddressForm
                formType={COMPANY_LEGAL_REPRESENTATIVES}
                components={{
                  salutation: null,
                  firstName: null,
                  middleName: null,
                  lastName: null,
                }}
              />
            </Col>
          </Row>
          <FormRow>
            <FormUploadComponentCol
              t={t}
              inputName={LEGAL_REPRESENTATIVE_UTILITY_BILL_DOCUMENT}
              uploadFormComponent={PersonDocumentUploadWithInputContainer}
              filterFunc={shouldRenderLegalRepUtilityBillDocument}
              colProps={{md: 12}}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo inputName={LEGAL_REPRESENTATIVE_UTILITY_BILL_DOCUMENT} t={t} />
                ),
                components: {
                  existingFilesList: ExistingFilesList,
                },
              }}
            />

            <FormUploadComponentCol
              t={t}
              inputName={LEGAL_REPRESENTATIVE_VISA_DOCUMENT}
              uploadFormComponent={PersonDocumentUploadWithInputContainer}
              filterFunc={shouldRenderLegalRepVisaDocumentFilter}
              colProps={{md: 12}}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo inputName={LEGAL_REPRESENTATIVE_VISA_DOCUMENT} t={t} />
                ),
                components: {
                  existingFilesList: ExistingFilesList,
                },
              }}
            />
          </FormRow>
        </>
      )}
    </>
  )
}

export default LegalRepresentativesFormContent
